import { ButtonWidget } from '@tableau/widgets';
import React from 'react';
import ReactDOM from 'react-dom';
import { classes } from 'typestyle/lib';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { FavoriteToggleClickedChildFunction, FavoriteToggleClickedTelemetryContext } from '~commonInfra/telemetryEvents/contexts/TelemetryContexts';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { ContentLocation, FavoriteToggleType } from '~commonInfra/telemetryEvents/types/ContentTelemetryTypes';
import { generateTelemetryEventHandler } from '~commonInfra/telemetryEvents/util/ContentTelemetry';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { PreviousUrlInitialState } from '~src/bootstrap/redux/reducers/PreviousUrlReducer';
import { ConnectedBroadcastButton } from '~src/broadcasts/components/ConnectedBroadcastButton';
import { Messages } from '~src/codegen/Localize';
import { currentUserIsAdmin } from '~src/spacesPlatform/utils/MixedContentHelpers';
import { IViewDetailInfo } from '~src/views/models/IViewInfo';

import { IContentInfo } from '../../models/IContentInfo';
import { SpaceInfo } from '../../state/SpaceInfo';
import { ConnectedBreadcrumbs } from '../breadcrumbs/ConnectedBreadcrumbs';
import { BackButton } from './BackButton';
import { ConnectedPageHeaderMenus } from './ConnectedPageHeaderMenus';
import { LeftItem, PageHeaderProps, pageHeaderSearchActiveStyle, Region, RightItem } from './PageHeader';
import { PageHeaderSearch, VizViewerSearchButton } from './PageHeaderSearch';
import { PageHeaderStyles } from './PageHeaderStyles';
import { isValidSpaceInfoForVizView, VizViewerFavoriteButton, VizViewerPageHeaderButtons } from './VizViewerPageHeaderButtons';
import CssVariables = PageHeaderStyles.CssVariables;
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';

const vizHeaderSearchOpenTestId = 'viz-header-search-open';
export const vizHeaderSearchOpenButtonTestId = vizHeaderSearchOpenTestId + '-Button';
const vizHeaderSearchCloseTestId = 'viz-header-search-close';
export const vizHeaderSearchCloseButtonTestId = vizHeaderSearchCloseTestId + '-Button';
export const vizHeaderContainerTestId = vizHeaderSearchCloseTestId + 'viz-header-container';

interface VizViewerPageHeaderState {
  readonly headerSearchStyle: string;
  readonly isSearchOpen: boolean;
  readonly vizViewerSearchButtonRef: HTMLElement | undefined;
}

export class VizViewerPageHeader extends React.PureComponent<PageHeaderProps, VizViewerPageHeaderState> {
  constructor(props: PageHeaderProps) {
    super(props);
    this.state = { isSearchOpen: false, vizViewerSearchButtonRef: undefined, headerSearchStyle: '' };
  }

  public render() {
    const parentId = this.getVizParentWorkbookId();

    const containerClasses = classes(
      PageHeaderStyles.shortHeaderTheme,
      PageHeaderStyles.offWhiteBackgroundColor,
      PageHeaderStyles.renderOnScreenOnly,
    );

    const containerStyles = isValidSpaceInfoForVizView(this.props.spaceInfo)
      ? { ...PageHeaderStyles.ContainerStyles, [CssVariables.headerHeight]: '38px' }
      : PageHeaderStyles.ContainerStyles;

    return (
      // TFSID 943709, 1357059: We need inline styling to have PageHeaderStyles.ContainerStyles to format the header in a background tab in order to serve the correct DSD layout.
      <div data-tb-test-id={vizHeaderContainerTestId} className={containerClasses} style={containerStyles}>
        <span className={PageHeaderStyles.LeftRegionVizViewer} >
          <Region className={PageHeaderStyles.BreadcrumbsAreaInFlexboxWithMargin} >
            {this.backButton(parentId)}
            {this.props.displayBreadCrumbs &&
              <LeftItem className={PageHeaderStyles.BreadcrumbsArea}>
                <ConnectedBreadcrumbs
                  trailingElements={[
                    ...this.shouldShowVizViewerFavoriteButton() ? [this.getVizViewerFavoriteButton()] : [],
                    ...this.shouldShowVizViewerBroadcastButton() ? [this.getVizViewerBroadcastButton()] : [],
                  ]}
                />
              </LeftItem>
            }
            {(!this.props.displayBreadCrumbs && this.shouldShowVizViewerFavoriteButton()) && this.getVizViewerFavoriteButton()}
            {(!this.props.displayBreadCrumbs && this.shouldShowVizViewerBroadcastButton()) && this.getVizViewerBroadcastButton()}
          </Region>
        </span>

        <span className={PageHeaderStyles.RightRegionVizViewer} >
          <Region>
            {!this.props.isInServerArea && this.vizViewerPageHeaderButtons()}
            <ConnectedPageHeaderMenus buttonType={'short'} showFavoritesMenu={this.shouldShowFavoritesMenu()} />
          </Region>
        </span>
      </div>
    );
  }

  private getVizViewerBroadcastButton(): JSX.Element {
    return (
        <ConnectedBroadcastButton {...this.props} />
    );
  }

  private getVizViewerFavoriteButton(): JSX.Element {
    return (
      <FavoriteToggleClickedTelemetryContext.Provider value={generateFavoriteToggleClickedTelemetryContext(this.props.spaceInfo)}>
        <VizViewerFavoriteButton {...this.props} />
      </FavoriteToggleClickedTelemetryContext.Provider>
    );
  }

  private shouldShowVizViewerFavoriteButton = (): boolean => {
    return !this.props.isOnServerStatusViz
      && !this.props.isOnSiteStatusViz
      && !_.isUndefined(this.props.spaceInfo.item);
  };

  private shouldShowVizViewerBroadcastButton = (): boolean => {
    return FeatureFlags.broadcastUI
      && FeatureFlags.broadcastEnabled
      && currentUserIsAdmin(getAppStore().getState())
      && this.props.spaceInfo.spaceType === 'view'
      && !this.props.isOnServerStatusViz
      && !this.props.isOnSiteStatusViz
      && !_.isUndefined(this.props.spaceInfo.item);
  };

  private shouldShowFavoritesMenu = (): boolean => {
    return this.shouldShowVizViewerFavoriteButton();
  };

  private setRef = (button: ButtonWidget) => {
    if (button) {
      this.setState({ vizViewerSearchButtonRef: ReactDOM.findDOMNode(button) as HTMLElement });
    }
  };

  private setVizViewerSearchButtonFocus = () => {
    if (!_.isUndefined(this.state.vizViewerSearchButtonRef)) {
      _.defer(() => {
        this.state.vizViewerSearchButtonRef.focus();
      });
    }
  };

  private vizViewerSearchButton = (): JSX.Element => {
    return (
      <RightItem>
        {VizViewerSearchButton({
          isSearchOpen: this.state.isSearchOpen,
          onClick: this.toggleSearchVisibility,
          title: this.state.isSearchOpen ? Messages.searchClose() : Messages.searchOpen(),
          testId: this.state.isSearchOpen ? vizHeaderSearchOpenTestId : vizHeaderSearchCloseTestId,
          setRef: this.setRef
        })}
      </RightItem>
    );
  };

  private vizViewerPageHeaderButtons = (): JSX.Element => {
    if (this.state.isSearchOpen) {
      return (
        <>
          <RightItem>{PageHeaderSearch({
            headerSearchStyle: this.state.headerSearchStyle,
            searchClosed: this.onSearchClosed,
            searchClosedWithoutNeedingFocus: this.onSearchClosedWithoutNeedingFocus,
            letSearchBoxHasFocus: true,
            isSearchInputBoxActive: this.isSearchInputBoxActive
          })}</RightItem>
          {this.vizViewerSearchButton()}
        </>
      );
    } else {
      const { isInVizViewerArea, addContentFavorite, removeContentFavorite, ...vizViewerButtonsProps } = this.props;
      return (
        <>
          <VizViewerPageHeaderButtons {...vizViewerButtonsProps} />
          { isValidSpaceInfoForVizView(this.props.spaceInfo) && <div className={PageHeaderStyles.VizViewerDivider} />}
          {this.vizViewerSearchButton()}
        </>
      );
    }
  };

  private isSearchInputBoxActive = (isActive: boolean) => {
    this.setState({headerSearchStyle: isActive ? pageHeaderSearchActiveStyle : ''});
  };

  private toggleSearchVisibility = () => {

    Telemetry.sendEvent<PageHeaderButtonClickEvent>({
      type: TelemetryEvents.PageHeaderButtonClick,
      button: 'viz-viewer-search',
      action: this.state.isSearchOpen ? 'close' : 'open'
    });

    this.setState((prevState) => {
      return { isSearchOpen: !prevState.isSearchOpen };
    });
  };

  // Borrowed from TopBar.ts
  private onSearchClosed = (event: BaseJQueryEventObject) => {
    if (this.state.isSearchOpen) {
      this.setState({ isSearchOpen: false }, () => {
        this.setVizViewerSearchButtonFocus();
      });
    }
  };

  private onSearchClosedWithoutNeedingFocus = (event: BaseJQueryEventObject) => {
    if (this.eventTargetOutOfSearch(event)) {
      if (this.state.isSearchOpen) {
        this.setState({ isSearchOpen: false });
      }
    }
  };

  private blurOmniboxTextBox() {
    $('input[name="omniboxTextBox"]').trigger('blur');
  }

  private eventTargetOutOfSearch(event: BaseJQueryEventObject) {
    const target = event && $(event.target);
    return !target || (target.closest('.tb-search-box-icon-button').length === 0 &&
      target.closest('.tb-omnibox-popup').length === 0);
  }

  private getVizParentWorkbookId() {
    const viewInfo: IViewDetailInfo = this.props.spaceInfo.item as IViewDetailInfo;
    const parentWorkbookId = viewInfo && viewInfo.workbook && viewInfo.workbook.id;
    return parentWorkbookId;
  }

  private backButton = (parentId: string): JSX.Element => {
    if (!this.shouldShowBackButton(parentId)) {
      return null;
    }

    return (
      <BackButton previousUrl={this.props.previousUrl}
        parentId={parentId}
        siteUrls={this.props.siteUrls}
        serverUrls={ServerUrls.getInstance()}
        isOnDatasourceAnalyticsViz={false}
        isOnSiteStatusViz={this.props.isOnSiteStatusViz}
        isOnServerStatusViz={this.props.isOnServerStatusViz}
        buttonType={'regular'}
      />);
  };

  private shouldShowBackButton = (parentId: string): boolean => {
    return !!parentId
      || this.props.previousUrl !== PreviousUrlInitialState
      || this.props.isOnServerStatusViz
      || this.props.isOnSiteStatusViz;
  };
}

export function generateFavoriteToggleClickedTelemetryContext(spaceInfo: SpaceInfo) {
  return function FavoriteToggleClickedTelemetryContext(childFunction: FavoriteToggleClickedChildFunction) {
    return (
      childFunction((toggleType: FavoriteToggleType) => {
        const eventHandler = generateTelemetryEventHandler(
          TelemetryEvents.FavoriteToggleClicked,
          spaceInfo,
          null, // array of IExplorableTypeInfo
          { id: spaceInfo.item.id, contentType: spaceInfo.item.contentType } as IContentInfo,
          ContentLocation.SpaceHeader,
          null, // content position
          null // ContentTelemetryContextSpecificData
        );
        eventHandler(toggleType);
      })
    );
  };
}
